// lib/storage.ts

interface StorageItem<T> {
  value: T
  expiry: number | null
}

export class StorageUtil {
  private prefix: string = 'MAKYO_'

  private getFullKey(key: string): string {
    return `${this.prefix}${key}`
  }

  setItem<T>(key: string, value: T, expiryDays?: number): void {
    const item: StorageItem<T> = {
      value: value,
      expiry: expiryDays ? Date.now() + expiryDays * 24 * 60 * 60 * 1000 : null,
    }
    localStorage.setItem(this.getFullKey(key), JSON.stringify(item))
  }

  getItem<T>(key: string): T | null {
    const itemStr = localStorage.getItem(this.getFullKey(key))
    if (!itemStr) return null

    try {
      const item: StorageItem<T> = JSON.parse(itemStr)
      if (item.expiry && Date.now() > item.expiry) {
        this.removeItem(key)
        return null
      }
      return item.value
    } catch (error) {
      console.error(`Error parsing stored item for key ${key}:`, error)
      return null
    }
  }

  removeItem(key: string): void {
    localStorage.removeItem(this.getFullKey(key))
  }

  removeExpiredItems(): void {
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i)
      if (storageKey?.startsWith(this.prefix)) {
        this.getItem(storageKey.slice(this.prefix.length)) // This will remove the item if it's expired
      }
    }
  }

  clear(): void {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key?.startsWith(this.prefix)) {
        localStorage.removeItem(key)
      }
    }
  }

  getAllItems<T>(): Record<string, T> {
    const items: Record<string, T> = {}
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(this.prefix)) {
        const value = this.getItem<T>(key.slice(this.prefix.length))
        if (value !== null) {
          items[key.slice(this.prefix.length)] = value
        }
      }
    }
    return items
  }
}
